import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { months } from '../../shared/constants/constants';
import { ANALYTICS_BODY_FONT_FAMILY } from './chartTypography';
import { getAnalyticsPaletteColor, getSemanticSeriesColor, withAlpha } from './analyticsColors';

const analyticsPlotStyle = { width: '100%', height: '100%', margin: 0, padding: 0 };
const analyticsPlotConfig = {
  responsive: true,
  displayModeBar: 'hover',
  displaylogo: false,
};
const getAnalyticsColorway = (paletteVariant = 'default') =>
  Array.from({ length: 10 }, (_, index) => getAnalyticsPaletteColor(index, { variant: paletteVariant }));

const applySemanticColorsToPlotlyData = (chartData, paletteVariant = 'default') => {
  return chartData.map((trace) => {
    const traceNameColor = getSemanticSeriesColor(trace?.name);
    const patchedTrace = { ...trace };

    if (traceNameColor) {
      patchedTrace.marker = {
        ...patchedTrace.marker,
        color: patchedTrace.type === 'bar' ? withAlpha(traceNameColor, 0.24) : traceNameColor,
      };
      patchedTrace.line = {
        ...patchedTrace.line,
        color: traceNameColor,
      };
    }

    if (Array.isArray(patchedTrace.labels)) {
      const semanticSliceColors = patchedTrace.labels.map((label, index) => {
        const semanticColor = getSemanticSeriesColor(label);
        const fallback = getAnalyticsPaletteColor(index, { variant: paletteVariant, label });
        return semanticColor || fallback;
      });
      patchedTrace.marker = {
        ...patchedTrace.marker,
        colors: semanticSliceColors,
      };
    }

    return patchedTrace;
  });
};

const pieLayout = (title, showTitle, paletteVariant = 'default') => {
  return {
    title: showTitle ? { text: title } : undefined,
    margin: {
      l: 15,
      r: 0,
      b: 15,
      t: showTitle ? 25 : 8,
    },
    autosize: true,
    yaxis: {
      rangemode: 'tozero',
      automargin: true,
      tickfont: {
        family: ANALYTICS_BODY_FONT_FAMILY,
        size: 12,
      },
    },
    xaxis: {
      automargin: true,
      tickfont: {
        family: ANALYTICS_BODY_FONT_FAMILY,
        size: 12,
      },
    },
    font: {
      family: ANALYTICS_BODY_FONT_FAMILY,
      size: 12,
    },
    legend: {
      font: {
        family: ANALYTICS_BODY_FONT_FAMILY,
        size: 12,
      },
    },
    hoverlabel: {
      bgcolor: '#FFFFFF',
      bordercolor: 'rgba(0, 0, 0, 0.10)',
      align: 'left',
      font: {
        family: ANALYTICS_BODY_FONT_FAMILY,
        size: 12,
        color: '#333333',
      },
    },
    colorway: getAnalyticsColorway(paletteVariant),
  };
};
const barLayout = (title, showTitle, paletteVariant = 'default') => {
  return {
    title: showTitle ? { text: title } : undefined,
    margin: {
      l: 15,
      r: 0,
      b: 15,
      t: showTitle ? 25 : 8,
    },
    autosize: true,
    yaxis: {
      rangemode: 'tozero',
      automargin: true,
      tickfont: {
        family: ANALYTICS_BODY_FONT_FAMILY,
        size: 12,
      },
    },
    xaxis: {
      automargin: true,
      tickfont: {
        family: ANALYTICS_BODY_FONT_FAMILY,
        size: 12,
      },
      dtick: 1,
    },
    font: {
      family: ANALYTICS_BODY_FONT_FAMILY,
      size: 12,
    },
    showlegend: false,

    legend: {
      x: 0,
      xanchor: 'left',
      y: 1,
      font: {
        family: ANALYTICS_BODY_FONT_FAMILY,
        size: 12,
      },
    },
    hoverlabel: {
      bgcolor: '#FFFFFF',
      bordercolor: 'rgba(0, 0, 0, 0.10)',
      align: 'left',
      font: {
        family: ANALYTICS_BODY_FONT_FAMILY,
        size: 12,
        color: '#333333',
      },
    },
    colorway: getAnalyticsColorway(paletteVariant),
  };
};
const lineLayout = (title, showTitle, paletteVariant = 'default') => {
  return {
    title: showTitle ? { text: title } : undefined,
    margin: {
      l: 15,
      r: 0,
      b: 15,
      t: showTitle ? 25 : 8,
    },
    autosize: true,
    yaxis: {
      rangemode: 'tozero',
      automargin: true,
      tickfont: {
        family: ANALYTICS_BODY_FONT_FAMILY,
        size: 12,
      },
    },
    xaxis: {
      automargin: true,
      tickfont: {
        family: ANALYTICS_BODY_FONT_FAMILY,
        size: 12,
      },
    },
    font: {
      family: ANALYTICS_BODY_FONT_FAMILY,
      size: 12,
    },
    showlegend: true,

    legend: {
      x: 1,
      y: 0.5,
      font: {
        family: ANALYTICS_BODY_FONT_FAMILY,
        size: 12,
      },
    },
    hoverlabel: {
      bgcolor: '#FFFFFF',
      bordercolor: 'rgba(0, 0, 0, 0.10)',
      align: 'left',
      font: {
        family: ANALYTICS_BODY_FONT_FAMILY,
        size: 12,
        color: '#333333',
      },
    },
    barmode: 'stack',
    colorway: getAnalyticsColorway(paletteVariant),
  };
};
function LinePlot({ chartData, title, type, showTitle = true, paletteVariant = 'default' }) {
  const layout = lineLayout(title, showTitle, paletteVariant);
  return (
    <Plot
      data={applySemanticColorsToPlotlyData(chartData, paletteVariant)}
      config={analyticsPlotConfig}
      useResizeHandler={true}
      layout={layout}
      style={analyticsPlotStyle}
    />
  );
}

function MonthLinePlot({ chartData, title, type, showTitle = true, paletteVariant = 'default' }) {
  const layout = lineLayout(title, showTitle, paletteVariant);
  layout.xaxis.categoryarray = months.filter((month) => {
    return chartData.findIndex((line) => line.x.includes(month)) >= 0;
  });
  layout.xaxis.categoryorder = 'array';
  return (
    <Plot
      data={applySemanticColorsToPlotlyData(chartData, paletteVariant)}
      config={analyticsPlotConfig}
      useResizeHandler={true}
      layout={layout}
      style={analyticsPlotStyle}
    />
  );
}

function BarPlot({ chartData, title, type, showTitle = true, paletteVariant = 'default' }) {
  const layout = barLayout(title, showTitle, paletteVariant);
  return (
    <Plot
      data={applySemanticColorsToPlotlyData(chartData, paletteVariant)}
      layout={layout}
      config={analyticsPlotConfig}
      useResizeHandler={true}
      style={analyticsPlotStyle}
    />
  );
}

function PieChart({ chartData, title, type, showTitle = true, paletteVariant = 'default' }) {
  const layout = pieLayout(title, showTitle, paletteVariant);
  return (
    <Plot
      data={applySemanticColorsToPlotlyData(chartData, paletteVariant)}
      layout={layout}
      config={analyticsPlotConfig}
      useResizeHandler={true}
      style={analyticsPlotStyle}
    />
  );
}

LinePlot.propTypes = {
  chartData: PropTypes.array,
  title: PropTypes.string,
  type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showTitle: PropTypes.bool,
  paletteVariant: PropTypes.string,
};

MonthLinePlot.propTypes = {
  chartData: PropTypes.array,
  title: PropTypes.string,
  type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showTitle: PropTypes.bool,
  paletteVariant: PropTypes.string,
};

BarPlot.propTypes = {
  chartData: PropTypes.array,
  title: PropTypes.string,
  type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showTitle: PropTypes.bool,
  paletteVariant: PropTypes.string,
};

PieChart.propTypes = {
  chartData: PropTypes.array,
  title: PropTypes.string,
  type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showTitle: PropTypes.bool,
  paletteVariant: PropTypes.string,
};

export { LinePlot, BarPlot, PieChart, MonthLinePlot };
